import _debounce2 from "lodash/debounce";
import React, { useEffect, useState, useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { AnalyticsContext, AnalyticsLocationConsumer, trackEvent } from 'latitude-analytics';
import { Box } from '@latitude/box';
import SvgInline from '@latitude/svg-inline';
import { Heading4 } from '@latitude/heading';
import { Text } from '@latitude/text';
import { ALIGN, BREAKPOINT, COLOR, MARGIN } from '@latitude/core/utils/constants';
import { pxAsNumber } from '@latitude/core/helpers';
import { List, ListItem } from '@latitude/list';
import { Card, CardIcon, CardHeading, CardBody, Section, Slider } from './FeaturesSlider.styled';
var responsiveConfig = [{
  breakpoint: pxAsNumber(BREAKPOINT.LG),
  settings: {
    slidesToShow: 3
  }
}, {
  breakpoint: pxAsNumber(BREAKPOINT.MD),
  settings: {
    slidesToShow: 2
  }
}, {
  breakpoint: pxAsNumber(BREAKPOINT.SM),
  settings: {
    slidesToShow: 1
  }
}];

var FeaturesSlider = function FeaturesSlider(_ref) {
  var id = _ref.id,
      className = _ref.className,
      _ref$data = _ref.data,
      data = _ref$data === void 0 ? [] : _ref$data,
      _ref$arrows = _ref.arrows,
      arrows = _ref$arrows === void 0 ? false : _ref$arrows,
      _ref$dots = _ref.dots,
      dots = _ref$dots === void 0 ? true : _ref$dots,
      _ref$infinite = _ref.infinite,
      infinite = _ref$infinite === void 0 ? false : _ref$infinite,
      _ref$speed = _ref.speed,
      speed = _ref$speed === void 0 ? 300 : _ref$speed,
      _ref$initialSlide = _ref.initialSlide,
      initialSlide = _ref$initialSlide === void 0 ? 0 : _ref$initialSlide,
      _ref$slidesToScroll = _ref.slidesToScroll,
      slidesToScroll = _ref$slidesToScroll === void 0 ? 1 : _ref$slidesToScroll,
      _ref$slidesToShow = _ref.slidesToShow,
      slidesToShow = _ref$slidesToShow === void 0 ? 4 : _ref$slidesToShow,
      _ref$responsive = _ref.responsive,
      responsive = _ref$responsive === void 0 ? responsiveConfig : _ref$responsive,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? COLOR.TRANSPARENT : _ref$backgroundColor,
      _ref$whiteTiles = _ref.whiteTiles,
      whiteTiles = _ref$whiteTiles === void 0 ? false : _ref$whiteTiles,
      heading = _ref.heading,
      subheading = _ref.subheading;

  var _useState = useState(null),
      cardHeadingHeight = _useState[0],
      setCardHeadingHeight = _useState[1];

  var _useContext = useContext(AnalyticsContext),
      analytics = _useContext[0];

  var equalizeHeadingHeight = function equalizeHeadingHeight() {
    var max = 0;
    data.forEach(function (card, i) {
      var element = document.getElementById("card-heading-" + i);

      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setCardHeadingHeight(max);
  };

  var handleWindowResize = function handleWindowResize() {
    setCardHeadingHeight(null); // resets height(s) to 'auto'

    equalizeHeadingHeight();
  };

  var debouncedHandleResize = _debounce2(handleWindowResize, 100);

  useEffect(function () {
    equalizeHeadingHeight();
    window.addEventListener('resize', debouncedHandleResize);
    return function () {
      return window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);
  var calculatedSlidesToShow = slidesToShow > data.length ? data.length : slidesToShow;
  var calculatedResponsive = responsive.map(function (config) {
    return Object.assign({}, config, {
      settings: Object.assign({}, config.settings, {
        slidesToShow: config.settings.slidesToShow > data.length ? data.length : config.settings.slidesToShow
      })
    });
  });
  return /*#__PURE__*/React.createElement(Section, {
    id: id,
    className: className,
    backgroundColor: backgroundColor
  }, /*#__PURE__*/React.createElement(Box, {
    isResponsive: true,
    margin: "0 auto"
  }, heading && /*#__PURE__*/React.createElement(Heading4, {
    align: ALIGN.CENTER,
    color: COLOR.BLACK,
    marginBottom: subheading ? MARGIN.M16 : MARGIN.M32
  }, ReactHtmlParser(heading)), subheading && /*#__PURE__*/React.createElement(Box, {
    marginBottom: MARGIN.M32
  }, /*#__PURE__*/React.createElement(Text, {
    align: ALIGN.CENTER,
    color: COLOR.BLACK
  }, subheading)), /*#__PURE__*/React.createElement(AnalyticsLocationConsumer, null, function (location) {
    return /*#__PURE__*/React.createElement(Slider, {
      arrows: arrows,
      dots: dots,
      infinite: infinite,
      initialSlide: initialSlide,
      slidesToScroll: slidesToScroll,
      slidesToShow: calculatedSlidesToShow,
      speed: speed,
      responsive: calculatedResponsive,
      whiteTiles: whiteTiles,
      afterChange: function afterChange(slideNumber) {
        // Get viewport
        var viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0); // Find which responsive breakpoint to determine number of slides in view
        // Then create label concatenating title from each slide

        var label = '';

        for (var i = responsiveConfig.length - 1; i >= 0; i--) {
          var config = responsiveConfig[i];

          if (viewportWidth <= config.breakpoint) {
            var slidesInViewport = data.slice(slideNumber, slideNumber + config.settings.slidesToShow);
            label = slidesInViewport.map(function (slide) {
              return slide.title;
            }).join('::');
            break;
          }
        }

        trackEvent(analytics, {
          category: 'slider',
          action: 'interaction',
          label: label,
          location: location
        });
      }
    }, data.map(function (card, i) {
      return /*#__PURE__*/React.createElement(Card, {
        key: i,
        className: whiteTiles ? 'white-tiles' : ''
      }, card.icon && /*#__PURE__*/React.createElement(CardIcon, null, /*#__PURE__*/React.createElement(SvgInline, {
        className: "h-100 w-100",
        name: card.icon
      })), /*#__PURE__*/React.createElement(CardHeading, {
        id: "card-heading-" + i,
        height: cardHeadingHeight ? cardHeadingHeight + "px" : 'auto'
      }, card.title), /*#__PURE__*/React.createElement(CardBody, null, card.text && /*#__PURE__*/React.createElement(Text, {
        align: ALIGN.CENTER,
        marginBottom: MARGIN.M16
      }, ReactHtmlParser(card.text)), card.list && /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(List, {
        dotColor: COLOR.BLUE_LIGHT,
        marginLeft: MARGIN.M32
      }, card.list.map(function (item) {
        return /*#__PURE__*/React.createElement(ListItem, null, ReactHtmlParser(item));
      }))), card.jsx));
    }));
  })));
};

export default FeaturesSlider;